/** @jsxImportSource @emotion/react */
import React, { useState, useEffect } from 'react';
import { Button } from '@mui/material';
import { Checkbox } from '@mui/material';
import { SubTitleLayout, SubTitleGroup } from 'components/layouts/ContentLayout';
import {
  SearchBoxRow,
  InputBox,
  SearchRows,
  SearchCols,
  SearchButtonWrap,
  SearchBox,
} from 'components/layouts/SearchBox';
import { IconButton } from 'components/buttons/IconSVG';
import { useTranslation } from 'react-i18next';
import { getCommonCodeNames } from 'apis/admin/CommonCode';
import { MultiComboBox } from 'components/selects/ComboBox';
import CustomInputWithSearch from 'components/inputs/CustomInputWithSearch';
import { useMessageBar } from 'hooks/useMessageBar';
import CustomGrid from 'components/grids/CustomGrid';
import { SearchParam, UtMatrixMasterList } from 'models/ut/UtMatrixList';
import { getUtMatrixMasterList } from 'apis/ut/UtMatrixList';
import { Code } from 'models/common/CommonCode';
import moment from 'moment';
import { st } from 'components/inputs/CustomInput';
import CustomDialog from 'components/modals/common/CustomDialog';
import { CommonUtil } from '../../../utils/CommonUtil';

type Props = {
  open: boolean;
  close: () => void;
  onCallback: (result) => void;
};

const UtMatrixListPopUp = ({ open, close, onCallback }: Props) => {
  const { t } = useTranslation();
  const { openMessageBar } = useMessageBar();

  const [flexRef, setFlexRef] = useState<any>();

  const [masterRowData, setMasterRowData] = useState<UtMatrixMasterList[]>([]);

  const [bildPlntCode, setBildPlntCode] = useState<Code[]>([]);
  // const [askPurpCode, setAskPurpCode] = useState<Code[]>([]);
  const [utmWkProgStatCode, setUtmWkProgStatCode] = useState<Code[]>([]);

  /**
   * @description UT Matrix List 팝업 조회
   * @author
   * -------------------------------------------------------------------------------------
   *  Date.       author         Description
   * -------------------------------------------------------------------------------------
   *  25.06.21    jihwan89       조회 날짜값 제거 - 두달전 1일 부터 오늘까지 기본값 적용되었던것 삭제
   *  25.10.20    jihwan89       UT 고도화 프로젝트 1) 이전버전보기 기본 체크
   *                                              2) 작성중, 작성완료, 검토중, 검토완료, 확정완료로 변경 (기존: 작성완료, 검토중, 확정완료)
   *                                              3) 새로고침은 기본 설정
   */

  const initSearchParam = {
    bildPlntCd: '',
    bildPlntCds: [],
    utmWkProgStatCodes: [], // 멀티셀렉트 전체
    utmWkProgStatCds: 'UTM02, UTM03, UTM04, UTM05, UTM07', // 초기 검색값
    utmWkProgStatCd: '',
    curYn: 'Y',
  };
  const [searchParam, setSearchParam] = useState<SearchParam>(initSearchParam);

  useEffect(() => {
    initCondition();
    btnSearch();
  }, []);

  const initCondition = async () => {
    const bildPlntCode = await getCommonCodeNames('BILD_PLNT_CD');
    // const askPurpCode = await getCommonCodeNames('ASK_PURP_CD');
    const utmWkProgStatCode = await getCommonCodeNames('UTM_WK_PROG_STAT_CD');
    setBildPlntCode(bildPlntCode);
    // setAskPurpCode(askPurpCode);
    console.log('utmWkProgStatCode', utmWkProgStatCode);
    const filteredWkProgStatCode = utmWkProgStatCode.filter((item) => {
      return ['UTM02', 'UTM03', 'UTM04', 'UTM05', 'UTM07'].includes(item?.cmnCd || '');
    });
    setUtmWkProgStatCode(filteredWkProgStatCode);
  };

  const masterLayoutDefinition = [
    {
      binding: 'utmId',
      header: String(t('ut.label.UTM ID', 'UTM ID')),
      visible: true,
      isReadOnly: true,
      align: 'center',
    },
    {
      binding: 'bildPlntCdNm',
      header: String(t('ut.label.공장', '공장')),
      isReadOnly: true,
      width: 180,
      align: 'center',
    },
    {
      binding: 'bildPlntCd',
      visible: false,
    },
    // {
    //   binding: 'askPurpCdNm',
    //   header: String(t('ut.label.목적', '목적')),
    //   isReadOnly: true,
    //   width: 100,
    //   align: 'center',
    // },
    {
      binding: 'askPurpCd',
      visible: false,
    },
    {
      binding: 'utmNo',
      header: String(t('ut.label.UT Matrix 번호', 'UT Matrix 번호')),
      width: 180,
      isReadOnly: true,
      align: 'center',
    },
    {
      binding: 'utmNm',
      header: String(t('ut.label.UT Matrix 명', 'UT Matrix 명')),
      width: '*',
      isReadOnly: true,
      align: 'left',
    },
    {
      binding: 'utmWkProgStatCdNm',
      header: String(t('ut.label.상태', '상태')),
      width: 100,
      isReadOnly: true,
      align: 'center',
      cssClass: 'WijmoTag',
      cellTemplate: (item) => {
        if (item.item.utmWkProgStatCd === 'UTM01') {
          return `<span class="grey">${item.value}</span>`;
        } else if (item.item.utmWkProgStatCd === 'UTM02') {
          return `<span class="green">${item.value}</span>`;
        } else if (item.item.utmWkProgStatCd === 'UTM03') {
          return `<span class="yellow">${item.value}</span>`;
        } else if (item.item.utmWkProgStatCd === 'UTM04') {
          return `<span class="blue">${item.value}</span>`;
        } else if (item.item.utmWkProgStatCd === 'UTM05') {
          return `<span class="orange">${item.value}</span>`;
        } else if (item.item.utmWkProgStatCd === 'UTM06') {
          return `<span class="red">${item.value}</span>`;
        } else {
          return `<span class="grey">${item.value}</span>`;
        }
      },
    },
    {
      binding: 'utmWkProgStatCd',
      visible: false,
    },
    {
      binding: 'tagIssuYn',
      header: String(t('ut.label.태그여부', '태그여부')),
      width: 120,
      isReadOnly: true,
      align: 'center',
    },
    {
      binding: 'verNo',
      header: String(t('com.label.버전', '버전')),
      width: 80,
      isReadOnly: true,
      align: 'center',
    },
    {
      binding: 'dataInsDtm',
      header: String(t('ut.label.요청일자', '요청일자')),
      width: 120,
      isReadOnly: true,
      align: 'center',
    },
  ];

  const onInitialized = (grid) => {
    setFlexRef(grid);

    grid.loadedRows.addHandler(() => {
      const items = grid.collectionView ? grid.collectionView.sourceCollection : grid.itemsSource;
      if (!items || !Array.isArray(items)) {
        return;
      }

      items.forEach((item, rowIndex) => {
        grid.columns.forEach((col, colIndex) => {
          const value = grid.getCellData(rowIndex, colIndex, false);
          const content = CommonUtil.safeHtmlContents(value, false);

          grid.setCellData(rowIndex, colIndex, content, true);
        });
      });

      grid.invalidate();
    });
  };

  const handleOnChange = (name, value) => {
    setSearchParam({ ...searchParam, [name]: value });
  };

  const onKeyDown = (e) => {
    if (e.key === 'Enter') {
      btnSearch();
    }
  };

  const btnSearch = () => {
    getUtMatrixMasterList(searchParam).then((result: UtMatrixMasterList[]) => {
      if (result !== null) {
        setMasterRowData(result);
      } else {
        openMessageBar({
          type: 'error',
          content: t(
            'com.msg.요청 정보 조회 중 오류가 발생했습니다.',
            '요청 정보 조회 중 오류가 발생했습니다.'
          ),
        });
      }
    });
  };

  const btnReload = () => {
    setSearchParam(initSearchParam);
  };

  const handleSave = () => {
    const selectedRowNodes = flexRef.selectedRows;
    const isSelected = selectedRowNodes.filter((item) => item.isSelected);
    const utmData = isSelected.map(({ dataItem }) => dataItem);
    onCallback(utmData);
    close();
  };

  const dialogButtons = [
    <Button
      key={'confirm'}
      css={IconButton.button}
      className="confirm"
      onClick={handleSave}
      disableRipple
    >
      {t('com.button.선택', '선택')}
    </Button>,
  ];

  return (
    <CustomDialog
      title={t('com.label.UT Matrix', 'UT Matrix')}
      size="lg"
      open={open}
      onClose={close}
      onCancel={close}
      buttons={dialogButtons}
    >
      <SearchBox>
        <SearchBoxRow>
          <InputBox>
            <SearchRows>
              <SearchCols>
                <label>{String(t('ut.label.공장', '공장'))}</label>
                <MultiComboBox
                  id="bildPlntCd"
                  options={bildPlntCode}
                  placeholder={String(t('com.label.전체', '전체'))}
                  defaultValue={searchParam?.bildPlntCds || []}
                  onChange={(value) => {
                    handleOnChange('bildPlntCd', value.join());
                  }}
                />
              </SearchCols>
              {/* <SearchCols>
                <label>{String(t('ut.label.목적', '목적'))}</label>
                <MultiComboBox
                  id="askPurpCd"
                  options={askPurpCode}
                  placeholder={String(t('com.label.전체', '전체'))}
                  defaultValue={searchParam?.askPurpCds}
                  onChange={(value) => {
                    handleOnChange('askPurpCd', value.join());
                  }}
                />
              </SearchCols> */}
              <SearchCols>
                <label>{String(t('ut.label.상태', '상태'))}</label>
                <MultiComboBox
                  id="utmWkProgStatCd"
                  options={utmWkProgStatCode}
                  placeholder={String(t('com.label.전체', '전체'))}
                  defaultValue={searchParam?.utmWkProgStatCodes}
                  onChange={(value) => {
                    handleOnChange('utmWkProgStatCd', value.join());
                  }}
                />
              </SearchCols>
              <SearchCols>
                <div style={{ display: 'inline-flex', width: '200px', paddingTop: '5px' }}>
                  <label style={{ paddingTop: '0px' }}>
                    {t('ut.label.이전버전보기', '이전버전보기')}
                  </label>
                  <Checkbox
                    id="showPrevVersion"
                    name="showPrevVersion"
                    css={st.checkbox}
                    className="custom"
                    checked={searchParam?.curYn === 'Y'}
                    onClick={(e) => {
                      setSearchParam({
                        ...searchParam,
                        curYn: searchParam?.curYn === 'Y' ? 'N' : 'Y',
                      });
                    }}
                    disableRipple
                  />
                </div>
              </SearchCols>
            </SearchRows>
            <SearchRows>
              <SearchCols>
                <label>{t('ut.label.UT Matrix 번호', 'UT Matrix 번호')}</label>
                <CustomInputWithSearch
                  type="text"
                  name="utmNo"
                  placeholder={String(
                    t('ut.label.UT Matrix 번호를 입력해 주세요.', 'UT Matrix 번호를 입력해 주세요.')
                  )}
                  value={searchParam?.utmNo}
                  onChange={(e) => handleOnChange(e.target.name, e.target.value)}
                  onKeyDown={onKeyDown}
                />
              </SearchCols>
              <SearchCols>
                <label>{t('ut.label.UT Matrix 명', 'UT Matrix 명')}</label>
                <CustomInputWithSearch
                  type="text"
                  name="utmNm"
                  placeholder={String(
                    t('ut.label.UT Matrix 명을 입력해 주세요.', 'UT Matrix 명을 입력해 주세요.')
                  )}
                  value={searchParam?.utmNm}
                  onChange={(e) => handleOnChange(e.target.name, e.target.value)}
                  onKeyDown={onKeyDown}
                />
              </SearchCols>
            </SearchRows>
          </InputBox>
          <SearchButtonWrap>
            <Button
              css={IconButton.button}
              className="reload"
              onClick={btnReload}
              disableRipple
            ></Button>
            <Button css={IconButton.button} className="find" onClick={btnSearch} disableRipple>
              {t('com.button.조회', '조회')}
            </Button>
          </SearchButtonWrap>
        </SearchBoxRow>
      </SearchBox>
      <SubTitleLayout>
        <SubTitleGroup>
          <h3>{t('ut.label.UT Matrix List', 'UT Matrix List')}</h3>
          <span className="total">
            {t('com.label.총', '총')}
            <span>{(masterRowData || []).length.toLocaleString()}</span>
            {t('com.label.건', '건')}
          </span>
        </SubTitleGroup>
      </SubTitleLayout>
      <CustomGrid
        layoutDefinition={masterLayoutDefinition}
        rowData={masterRowData}
        initialized={onInitialized}
        height={250}
        autoCheck={true}
        allowPinning={false}
      />
    </CustomDialog>
  );
};

export default UtMatrixListPopUp;
